import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Image,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import {
  ComplianceItem,
  ComplianceResult,
} from '../../types/twilio-onboarding';

const { Text, Paragraph } = Typography;

interface ComplianceCheckPanelProps {
  loading: boolean;
  result: ComplianceResult | null;
  onRunCheck: () => void;
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<string, { color: string; label: string }> = {
    ready: { color: 'green', label: 'Ready' },
    blocked: { color: 'red', label: 'Blocked' },
    needs_review: { color: 'orange', label: 'Needs Review' },
  };
  const { color, label } = config[status] ?? {
    color: 'default',
    label: status,
  };
  return (
    <Tag color={color} style={{ fontSize: 14, padding: '4px 12px' }}>
      {label}
    </Tag>
  );
};

const CheckItem: React.FC<{ label: string; item: ComplianceItem }> = ({
  label,
  item,
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      marginBottom: 8,
    }}
  >
    {item.pass ? (
      <CheckCircleFilled style={{ color: '#52c41a', marginTop: 4 }} />
    ) : (
      <CloseCircleFilled style={{ color: '#f5222d', marginTop: 4 }} />
    )}
    <div>
      <Text strong>{label}</Text>
      <br />
      <Text style={{ fontSize: 13 }} type="secondary">
        {item.detail}
      </Text>
    </div>
  </div>
);

const CheckSection: React.FC<{
  title: string;
  checks: Record<string, ComplianceItem>;
}> = ({ title, checks }) => {
  const allPass = Object.values(checks).every(c => c.pass);
  return (
    <Collapse.Panel
      header={
        <Space>
          {allPass ? (
            <CheckCircleFilled style={{ color: '#52c41a' }} />
          ) : (
            <ExclamationCircleFilled style={{ color: '#faad14' }} />
          )}
          <Text strong>{title}</Text>
        </Space>
      }
      key={title}
    >
      {Object.entries(checks).map(([key, item]) => (
        <CheckItem
          item={item}
          key={key}
          label={key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
        />
      ))}
    </Collapse.Panel>
  );
};

export const ComplianceCheckPanel: React.FC<ComplianceCheckPanelProps> = ({
  loading,
  result,
  onRunCheck,
}) => {
  const [showTimeoutMessage, setShowTimeoutMessage] = useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const loadingStartRef = useRef<number | null>(null);

  useEffect(() => {
    if (loading) {
      loadingStartRef.current = Date.now();
      setShowTimeoutMessage(false);
      setShowTimeoutWarning(false);

      const timer60 = setTimeout(() => setShowTimeoutMessage(true), 60_000);
      const timer120 = setTimeout(() => setShowTimeoutWarning(true), 120_000);

      return () => {
        clearTimeout(timer60);
        clearTimeout(timer120);
      };
    }
    loadingStartRef.current = null;
    setShowTimeoutMessage(false);
    setShowTimeoutWarning(false);
  }, [loading]);

  return (
    <Card
      extra={
        <Button
          disabled={loading}
          onClick={onRunCheck}
          style={{ backgroundColor: '#5b1eaf', borderColor: '#5b1eaf' }}
          type="primary"
        >
          {result ? 'Re-run Check' : 'Run Compliance Check'}
        </Button>
      }
      style={{ marginBottom: 24 }}
      title="Compliance Check"
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: 16, color: '#666' }}>
            Running compliance check. This involves loading the website and
            analyzing its content...
          </Paragraph>
          {showTimeoutMessage && (
            <Alert
              message="This is taking longer than usual. Please continue to wait..."
              showIcon
              style={{ marginTop: 16 }}
              type="info"
            />
          )}
          {showTimeoutWarning && (
            <Alert
              message="The check may have timed out. You can try again or proceed without it."
              showIcon
              style={{ marginTop: 8 }}
              type="warning"
            />
          )}
        </div>
      )}

      {!loading && result && (
        <>
          <div style={{ marginBottom: 16 }}>
            <Space size="middle">
              <Text strong>Overall Status:</Text>
              <StatusBadge status={result.overall_status} />
              {result.campaign_type_recommendation && (
                <>
                  <Text strong>Recommended Campaign Type:</Text>
                  <Tag color="blue">{result.campaign_type_recommendation}</Tag>
                </>
              )}
            </Space>
          </div>

          {result.failures.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              {result.failures.map((f, i) => (
                <Alert
                  description={f.fix}
                  key={i}
                  message={
                    <span>
                      <Text strong>{f.item}</Text>
                      {f.error_code && (
                        <Tag color="red" style={{ marginLeft: 8 }}>
                          Error {f.error_code}
                        </Tag>
                      )}
                    </span>
                  }
                  showIcon
                  style={{ marginBottom: 8 }}
                  type="error"
                />
              ))}
            </div>
          )}

          <Collapse
            defaultActiveKey={
              result.overall_status !== 'ready' ? ['Opt-In Form'] : []
            }
          >
            <CheckSection checks={result.opt_in_form} title="Opt-In Form" />
            <CheckSection
              checks={result.privacy_policy}
              title="Privacy Policy"
            />
            <CheckSection
              checks={result.terms_of_service}
              title="Terms of Service"
            />
          </Collapse>

          {result.client_message && (
            <Card
              size="small"
              style={{ marginTop: 16, backgroundColor: '#fafafa' }}
              title="Client Message (copyable)"
            >
              <Paragraph
                copyable
                style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}
              >
                {result.client_message}
              </Paragraph>
            </Card>
          )}

          {result.screenshot_url && (
            <div style={{ marginTop: 16 }}>
              <Text strong>Screenshot:</Text>
              <div style={{ marginTop: 8 }}>
                <Image
                  src={result.screenshot_url}
                  style={{ borderRadius: 4, border: '1px solid #d9d9d9' }}
                  width={300}
                />
              </div>
            </div>
          )}
        </>
      )}

      {!loading && !result && (
        <Paragraph type="secondary">
          Run a compliance check to analyze the client's website for Twilio A2P
          10DLC requirements. This checks the opt-in form, privacy policy, and
          terms of service.
        </Paragraph>
      )}
    </Card>
  );
};
